import { Box, Button, FormControlLabel, IconButton, Modal, Switch, TextField, Typography } from "@mui/material"
import { useEffect, useState } from "react";
import modalStyle from "../../styles/modalStyle";
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import apiClient from "../../axiosClient";
import DropDown from "../baseComponents/dropdown";
import { decodedJWT, getUserRole } from "../../utils/commonUtils";
import { numberPattern, passwordPattern, textPattern } from "../../constants/patterns";
import User, { UserRequest, EmploymentType } from "../../interfaces/user";
import RoleManagement from "../../security/roleManagement";
import { userRoles } from "../../constants/userRoles";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { useAppDispatch } from "../../store/hooks";
import { loadUsers } from "../../store/slices/userSlice";
import { protectedTextFieldProps } from "../../utils/protectedTextFieldProps";
import UserService from "../../services/userService";

interface ModalProps {
    openModal: boolean,
    userToUpdate: User,
    handleCloseModal: () => void,
    reloadFunction: () => void
}

const UpdateUserModal: React.FC<ModalProps> = ({openModal, userToUpdate, handleCloseModal, reloadFunction}) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [user, setUser] = useState<UserRequest>({
        name: "",
        email: "",
        phone_number: "",
        address: "",
        user_role: "",
        password: null,
        parent_username: "",
        is_temporarily_locked: false,
        employment_type: null
    });

    useEffect(() => {
        setUser({
            name: userToUpdate.name,
            email: userToUpdate.email,
            phone_number: userToUpdate.phone_number,
            address: userToUpdate.address,
            user_role: userToUpdate.user_role,
            password: null,
            parent_username: userToUpdate.parent_username,
            is_temporarily_locked: userToUpdate.is_temporarily_locked,
            employment_type: userToUpdate.employment_type ?? null
        });
    }, [userToUpdate, openModal])
    
    const users = useSelector((state: RootState) => state.userList.users);
    const token = decodedJWT();
    const userRole = getUserRole(token);
    const dispatch: AppDispatch = useAppDispatch();

    const userRoleOptions = RoleManagement.isSuperAdmin(userRole)
        ? userRoles.adminUserRoles
        : token && token.owner_type === 'Client'
        ? userRoles.clientUserRoles
        : token && token.owner_type === 'Agency'
        ? userRoles.agencyUserRoles
        : [];

    const handleSetAgent = <K extends keyof UserRequest> (key: K, value:UserRequest[K]) => {
        setUser((prevAgent) => ({ ...prevAgent, [key]: value }));
    }

    // Handle form submission
    const handleSubmit = async () => {
        try {
            const payload: UserRequest = { ...user };
            if (!payload.name || !payload.address || !payload.phone_number || !payload.user_role || !payload.parent_username || !payload.employment_type) {
                alert('Please fill all required values');
                return;
            }

            if(payload.password && !passwordPattern.test(payload.password)){
                alert('Please satisfy the following conditions for password:\nAtleast 1 capital letter\nAtleast 1 digit\nAtleast 1 special character\nAtleast 8 characters\nNo spaces');
                return;
            }
            const response = await apiClient.patch(`/api/v1/user/${userToUpdate.username}`, { ...payload });
            if(response.status === 200){
                alert("Successfully Updated User"); 
                await fetchUserList();
                reloadFunction();
                handleCloseModal(); // Close the modal on success
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        }
    };

    const fetchUserList = async () => {
        const query = RoleManagement.isSuperAdmin(userRole) ? '?user_role=Admin' : '';
        const response = await UserService.getUsers(query);
        if (response) {
            dispatch(loadUsers(response.user_responses as User[]));
        }
    };

    const closeModal = () => {
        setUser({
            name: "",
            email: "",
            phone_number: "",
            address: "",
            user_role: "",
            password: "",
            parent_username: "",
            is_temporarily_locked: false,
            employment_type: null
        });
        handleCloseModal();
    };

    return (
        <Modal open={openModal} onClose={closeModal} >
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography id="modal-title" variant="h6">
                        Update {RoleManagement.isSuperAdmin(userRole) ? 'Admin' : 'User'} {" "}
                            <Box component="span" sx={{ color: "blue", fontWeight: 600}}>
                                {userToUpdate.username}
                            </Box>
                    </Typography>
                    <IconButton onClick={closeModal}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box mt={2} display="flex" justifyContent="space-between">
                    <Box width="30%">
                        <TextField
                            label="Name"
                            value={user.name}
                            onChange={(e) => handleSetAgent('name', e.target.value.replace(textPattern, ""))}
                            fullWidth
                            margin="normal"
                            slotProps={{
                                htmlInput: {
                                    maxLength: 50,
                                },
                            }}
                        />
                    </Box>
                    <Box width="30%">
                        <DropDown 
                            label="User Role" 
                            items={user.user_role === "Admin" ? ["Admin"] : userRoleOptions}
                            selectedValue={user.user_role} 
                            onSelection={(e) => handleSetAgent('user_role', e)} 
                            disabled={user.user_role === 'Admin'}
                        />
                    </Box>
                    <Box width="30%">
                        <TextField
                            label="Password"
                            value={user.password}
                            type={showPassword ? 'text' : 'password'}
                            onChange={(e) => handleSetAgent('password', e.target.value)}
                            fullWidth
                            margin="normal"
                            slotProps={{
                                htmlInput: {
                                    ...protectedTextFieldProps,
                                    maxLength: 30,
                                },
                                input: {
                                    endAdornment: (
                                        <IconButton onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    )
                                }
                            }}
                        />
                    </Box>
                </Box>
                <Box mt={2} display="flex" justifyContent="space-between">
                    <Box width="48%">
                        <DropDown
                            label="Employment Type"
                            selectedValue={user.employment_type ?? null}
                            onSelection={(val) => handleSetAgent('employment_type', val)}
                            items={Object.values(EmploymentType)}
                        />
                    </Box>
                </Box>
                <Box mt={2} display="flex" justifyContent="space-between">
                    <Box width="48%">
                        <TextField
                            label="Email"
                            value={user.email}
                            type="email"
                            onChange={(e) => handleSetAgent('email', e.target.value.replace(textPattern, ""))}
                            fullWidth
                            margin="normal"
                            slotProps={{
                                htmlInput: {
                                    maxLength: 100,
                                },
                            }}
                        />
                    </Box>
                    <Box width="48%">
                        <TextField
                            label="Phone Number"
                            value={user.phone_number}
                            type="tel"
                            onChange={(e) => handleSetAgent('phone_number', e.target.value.replace(numberPattern, "").slice(0, 10))}
                            fullWidth
                            margin="normal"
                        />
                    </Box>
                </Box>
                <Box mt={2} display="flex" justifyContent="space-between">
                    <Box width="100%">
                        <TextField
                            label="Address"
                            value={user.address}
                            type="text"
                            onChange={(e) => handleSetAgent('address', e.target.value.replace(textPattern, ""))}
                            fullWidth
                            margin="normal"
                            slotProps={{
                                htmlInput: {
                                    maxLength: 1000,
                                },
                            }}
                        />
                    </Box>
                </Box>

                {userToUpdate.is_temporarily_locked && (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={user.is_temporarily_locked}
                                onChange={e => handleSetAgent('is_temporarily_locked', e.target.checked)}
                                name="Is Temporarily Locked"
                                size="medium"
                            />
                        }
                        sx={{ mt: 2 }}
                        label="Deselect to unlock user's account"
                        labelPlacement="end"
                    />
                )}
                
                {token && token.owner_type === 'Client' && (
                    <Box mt={2} display="flex" justifyContent="space-between">
                        <Box width="100%">
                            <DropDown
                                label="Select Parent"
                                items={users}               // an array of User
                                keyProp="username"                   // MenuItem key={item.username}
                                valueProp="username"                 // MenuItem value={item.username}
                                displayProp="username"              // renders item.username
                                selectedValue={user.parent_username}     // the currently‐selected username
                                onSelection={(val: string) =>       // val will be the selected username
                                    handleSetAgent("parent_username", val)
                                }
                            />
                        </Box>
                    </Box>
                )}
                
                <Box mt={2} textAlign="right">
                    <Button variant="contained" color="primary" onClick={handleSubmit}>
                        Submit
                    </Button>
                </Box>
            </Box>
        </Modal >
    );
}

export default UpdateUserModal;